"use client";
import { JSX, useEffect } from "react";
import { initContentSdk } from "@sitecore-content-sdk/nextjs";
import { eventsPlugin } from "@sitecore-content-sdk/events";
import { analyticsBrowserAdapter, analyticsPlugin } from "@sitecore-content-sdk/analytics-core";
import config from "sitecore.config";
import { useConsent } from "lib/consent/useConsent";

/** One-shot guard: the SDK must never be initialized twice in a page lifetime. */
let cloudSdkInitialized = false;

const Bootstrap = ({
  siteName,
  isPreviewMode,
}: {
  siteName: string;
  isPreviewMode: boolean;
}): JSX.Element | null => {
  // CloudSDK is initialized with `enableBrowserCookie: true`, which writes a
  // persistent `sc_<id>` identifier the moment initialize() runs — before any
  // pageView event. Gating CdpPageView alone therefore leaves an identifier on
  // every visitor regardless of consent, so the SDK itself must be gated too.
  const hasConsent = useConsent("targeting");

  useEffect(() => {
    if (cloudSdkInitialized) return;

    if (process.env.NODE_ENV === "development") {
      console.debug(
        "Browser Events SDK is not initialized in development environment",
      );
      return;
    }

    if (isPreviewMode) {
      console.debug(
        "Browser Events SDK is not initialized in edit and preview modes",
      );
      return;
    }

    if (!hasConsent)
      console.debug(
        "Browser Events SDK is not initialized without targeting consent",
      );

    if (config.api.edge?.clientContextId) {
      initContentSdk({
        config: {
          contextId: config.api.edge.clientContextId,
          edgeUrl: config.api.edge.edgeUrl,
          siteName: siteName || config.defaultSite,
        },
        plugins: [
          analyticsPlugin({
            options: {
              enableCookie: true,
              cookieDomain: window.location.hostname.replace(/^www\./, ""),
            },
            adapter: analyticsBrowserAdapter(),
          }),
          eventsPlugin(),
        ],
      });
      cloudSdkInitialized = true;
    } else {
      console.error("Client Edge API settings missing from configuration");
    }
  }, [siteName, isPreviewMode, hasConsent]);

  return null;
};

export default Bootstrap;
